import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Eye, Save, FileText, 
  Trash2, Settings2, Plus, X,
  Type, AlignLeft, Mail, Hash, ChevronDown, 
  ListTodo, CircleDot, CheckSquare, Calendar, 
  Clock, Paperclip, Link2, Phone, ArrowDown, ArrowUp,
  Wand2, MoreVertical, SquarePen, BookOpen
} from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { MultiSelect } from "../../components/ui/multi-select";
import { cn } from "../../components/ui/utils";
import { toast } from "sonner";
import api from "../../utils/api";
import { DynamicFormFields } from "../../components/service-desk/DynamicFormFields";
import { useData } from "../../context/DataContext";

import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "../../components/ui/dialog";

interface FormField {
  id: string;
  type: string;
  label: string;
  description: string;
  placeholder: string;
  required: boolean;
  options?: string[];
  showContainer?: boolean;
  containerColor?: 'gray' | 'blue' | 'green' | 'amber' | 'red';
  showDivider?: boolean;
  selectionMode?: 'single' | 'multi';
  dataSource?: string;
}

const FIELD_TYPES = [
  { type: 'short_text', label: 'Short Text', defaultLabel: 'New text field', icon: <Type className="w-4 h-4" />, emoji: '📝' },
  { type: 'long_text', label: 'Long Text', defaultLabel: 'New textarea field', icon: <AlignLeft className="w-4 h-4" />, emoji: '📄' },
  { type: 'email', label: 'Email', defaultLabel: 'New email field', icon: <Mail className="w-4 h-4" />, emoji: '📧' },
  { type: 'number', label: 'Number', defaultLabel: 'New number field', icon: <Hash className="w-4 h-4" />, emoji: '🔢' },
  { type: 'dropdown', label: 'Dropdown', defaultLabel: 'New select field', icon: <ChevronDown className="w-4 h-4" />, emoji: '🔽' },
  { type: 'multi_select', label: 'Multi-Select', defaultLabel: 'New multiselect field', icon: <ListTodo className="w-4 h-4" />, emoji: '☑️' },
  { type: 'radio', label: 'Radio Button', defaultLabel: 'New radio field', icon: <CircleDot className="w-4 h-4" />, emoji: '🔘' },
  { type: 'checkbox', label: 'Checkbox', defaultLabel: 'New checkbox field', icon: <CheckSquare className="w-4 h-4" />, emoji: '✅' },
  { type: 'date', label: 'Date', defaultLabel: 'New date field', icon: <Calendar className="w-4 h-4" />, emoji: '📅' },
  { type: 'time', label: 'Time', defaultLabel: 'New time field', icon: <Clock className="w-4 h-4" />, emoji: '⏰' },
  { type: 'file', label: 'File Upload', defaultLabel: 'New file field', icon: <Paperclip className="w-4 h-4" />, emoji: '📎' },
  { type: 'url', label: 'URL', defaultLabel: 'New url field', icon: <Link2 className="w-4 h-4" />, emoji: '🔗' },
  { type: 'phone', label: 'Phone', defaultLabel: 'New phone field', icon: <Phone className="w-4 h-4" />, emoji: '📞' },
  { type: 'idarah_data', label: 'Idarah Data', defaultLabel: 'System Reference Data', icon: <Wand2 className="w-4 h-4" />, emoji: '✨' },
  { type: 'static_text', label: 'Static Text', defaultLabel: 'Information text', icon: <AlignLeft className="w-4 h-4" />, emoji: 'ℹ️' },
  { type: 'heading', label: 'Heading', defaultLabel: 'Section Heading', icon: <Type className="w-4 h-4" />, emoji: '🏷️' },
];

export default function CreateRequestForm() {
  const navigate = useNavigate();
  const { deskId, formId } = useParams<{ deskId: string, formId?: string }>();
  
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [fields, setFields] = useState<FormField[]>([]);
  const [linkedProcedureIds, setLinkedProcedureIds] = useState<string[]>([]);
  const [procedures, setProcedures] = useState<any[]>([]);
  const [formLinkedProcedures, setFormLinkedProcedures] = useState<any[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // Combine procedures user has permission for with procedures already in the form
  const combinedProcedures = useMemo(() => {
    const map = new Map();
    
    // Add selectable procedures (user has permission)
    procedures.forEach(p => map.set(String(p.id), p));
    
    // Add already linked procedures (even if user lacks global permission)
    // Only if they are currently in the selection
    formLinkedProcedures.forEach(p => {
      const id = String(p.id);
      if (!map.has(id) && linkedProcedureIds.includes(id)) {
        map.set(id, p);
      }
    });
    
    return Array.from(map.values()).sort((a, b) => 
      (a.procedureName || "").localeCompare(b.procedureName || "")
    );
  }, [procedures, formLinkedProcedures, linkedProcedureIds]);
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);
  const [versionInfo, setVersionInfo] = useState({ number: 1, status: 'Draft', id: '' });

  // Track initial state for "dirty" check
  const [initialData, setInitialData] = useState<{name: string, description: string, fields: FormField[], linkedProcedureIds: string[]}>({
    name: "",
    description: "",
    fields: [],
    linkedProcedureIds: []
  });

  useEffect(() => {
    const fetchProcedures = async () => {
      try {
        const response = await api.get('/api/service-desk/reference-data/procedures?context=builder');
        if (response.success) {
          setProcedures(response.data || []);
        }
      } catch (err) {
        console.error("Error fetching procedures for selector:", err);
      }
    };
    fetchProcedures();
  }, []);

  useEffect(() => {
    if (formId) {
      fetchFormData();
    }
  }, [formId]);

  const [previewValues, setPreviewValues] = useState<Record<string, any>>({});

  const fetchFormData = async () => {
    try {
      const response = await api.get(`/api/service-desk/forms/${formId}`);
      if (response.success) {
        setFormName(response.form.name);
        setFormDescription(response.form.description);
        
        const versions = response.form.versions || [];
        const latest = versions.sort((a: any, b: any) => b.versionNumber - a.versionNumber)[0];
        
        if (latest) {
          const loadedFields = latest.fields || [];
          const loadedLinkedProcedures = latest.linkedProcedures || [];
          const loadedLinkedProcedureIds = loadedLinkedProcedures.map((p: any) => String(p.id));
          
          setFields(loadedFields);
          setLinkedProcedureIds(loadedLinkedProcedureIds);
          setFormLinkedProcedures(loadedLinkedProcedures);
          
          setVersionInfo({
            number: latest.versionNumber,
            status: latest.status.charAt(0).toUpperCase() + latest.status.slice(1).toLowerCase(),
            id: latest.id.toString()
          });
          
          setInitialData({
            name: response.form.name,
            description: response.form.description || "",
            fields: JSON.parse(JSON.stringify(loadedFields)),
            linkedProcedureIds: loadedLinkedProcedureIds
          });
        }
      }
    } catch (error) {
      console.error("Error fetching form:", error);
      toast.error("Failed to load form data.");
    }
  };

  const isDirty = useMemo(() => {
    return formName !== initialData.name || 
           formDescription !== initialData.description || 
           JSON.stringify(fields) !== JSON.stringify(initialData.fields) ||
           JSON.stringify(linkedProcedureIds.sort()) !== JSON.stringify(initialData.linkedProcedureIds.sort());
  }, [formName, formDescription, fields, linkedProcedureIds, initialData]);

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Please enter a form name.");
      return;
    }

    setIsSaving(true);
    try {
      let response;
      if (!formId) {
        response = await api.post(`/api/service-desk/${deskId}/forms`, {
          name: formName,
          description: formDescription,
          fields,
          linkedProcedureIds
        });
        if (response.success) {
          toast.success("Form created and saved as draft.");
          navigate(`/service-desk/${deskId}/forms/${response.form.id}/edit`, { replace: true });
        }
      } else {
        response = await api.post(`/api/service-desk/forms/${formId}/versions`, {
          fields,
          name: formName,
          description: formDescription,
          linkedProcedureIds
        });
        if (response.success) {
          setVersionInfo({
            number: response.version.versionNumber,
            status: 'Draft',
            id: response.version.id.toString()
          });
          setInitialData({
            name: formName,
            description: formDescription,
            fields: JSON.parse(JSON.stringify(fields)),
            linkedProcedureIds: [...linkedProcedureIds]
          });
          toast.success(`Version ${response.version.versionNumber} saved as draft.`);
        }
      }
      return response;
    } catch (error) {
      toast.error("An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    const saveRes = await handleSave();
    if (!saveRes?.success) return;

    const versionId = saveRes.version ? saveRes.version.id : saveRes.draft.id;
    try {
      const pubRes = await api.patch(`/api/service-desk/forms/versions/${versionId}/publish`, {});
      if (pubRes.success) {
        toast.success("Form published successfully!");
        navigate(`/service-desk/settings/${deskId}`);
      }
    } catch (error) {
      toast.error("Failed to publish.");
    }
  };

  const addField = (fieldType: string) => {
    const typeInfo = FIELD_TYPES.find(f => f.type === fieldType);
    const newField: FormField = {
      id: Math.random().toString(36).substr(2, 9),
      type: fieldType,
      label: typeInfo?.defaultLabel || "New field",
      description: fieldType === 'file' ? "Max 10 files, 10MB each (PDF, JPG, PNG)" : "",
      placeholder: "",
      required: false,
      options: ['dropdown', 'multi_select', 'radio', 'checkbox'].includes(fieldType) ? ['Option 1', 'Option 2'] : undefined,
      showContainer: fieldType === 'static_text',
      containerColor: fieldType === 'static_text' ? 'gray' : undefined,
      showDivider: fieldType === 'heading',
      selectionMode: fieldType === 'idarah_data' ? 'single' : undefined,
      dataSource: fieldType === 'idarah_data' ? 'assets' : undefined
    };
    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
  };

  const removeField = (id: string) => {
    setFields(fields.filter(f => f.id !== id));
    if (selectedFieldId === id) setSelectedFieldId(null);
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFields(newFields);
  };

  const selectedField = fields.find(f => f.id === selectedFieldId);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] lg:h-screen overflow-hidden bg-background -m-4 md:-m-6 lg:-mt-6">
      <div className="p-6 border-b border-border bg-white shrink-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-accent rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">{formId ? "Edit Request Form" : "Create Request Form"}</h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Version {versionInfo.number}</span>
                <span>•</span>
                <span className={cn("font-medium", versionInfo.status === 'Published' ? "text-green-600" : "text-yellow-600")}>{versionInfo.status}</span>
              </div>
            </div>
          </div>
          {/* Desktop Actions */}
          <div className="hidden sm:flex items-center gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setIsPreview(!isPreview)}>
              {isPreview ? <><Wand2 className="w-4 h-4" /> Edit</> : <><Eye className="w-4 h-4" /> Preview</>}
            </Button>
            <Button variant="outline" className="gap-2" disabled={isSaving || !isDirty} onClick={handleSave}>
              <Save className="w-4 h-4" /> {isSaving ? "Saving..." : "Save as Draft"}
            </Button>
            <Button className="gap-2 bg-primary text-white" onClick={handlePublish}>
              <FileText className="w-4 h-4" /> Publish
            </Button>
          </div>

          {/* Mobile Actions (CTA Ellipsis) */}
          <div className="sm:hidden flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 border-slate-200">
                  <MoreVertical className="w-5 h-5 text-slate-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2 space-y-1">
                <DropdownMenuItem onClick={() => setIsPreview(!isPreview)} className="rounded-lg h-11 font-bold gap-3">
                  {isPreview ? <><Wand2 className="w-4 h-4 text-slate-600" /> Edit Form</> : <><Eye className="w-4 h-4 text-slate-600" /> Preview Form</>}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSave} 
                  disabled={isSaving || !isDirty}
                  className="rounded-lg h-11 font-bold gap-3"
                >
                  <Save className="w-4 h-4 text-slate-600" /> {isSaving ? "Saving..." : "Save as Draft"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePublish} className="rounded-lg h-11 font-bold gap-3 text-primary focus:text-primary">
                  <FileText className="w-4 h-4 text-primary" /> Publish Form
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden w-full">
        {isPreview ? (
          <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
            <div className="max-w-2xl mx-auto">
              <div className="bg-white border border-border rounded-xl p-8 space-y-8 shadow-sm text-left">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{formName || "Untitled Form"}</h2>
                  {formDescription && <p className="text-muted-foreground">{formDescription}</p>}
                </div>
                
                <DynamicFormFields 
                    fields={fields}
                    values={previewValues}
                    onChange={(id, val) => setPreviewValues(prev => ({ ...prev, [id]: val }))}
                    isPreview={true}
                    showSectionTitle={false}
                    emptyMessage="No fields added yet."
                />

                <div className="pt-6 border-t border-border"><Button className="w-full h-11 bg-primary text-white text-base font-bold opacity-50 cursor-not-allowed" disabled>Submit Request</Button></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Left Sidebar - Hidden on mobile */}
            <div className="hidden lg:block w-72 border-r border-border bg-white overflow-y-auto shrink-0">
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-4 px-1">Field Types</h3>
                <div className="grid grid-cols-1 gap-1">
                  {FIELD_TYPES.map((f) => (
                    <button key={f.type} onClick={() => addField(f.type)} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50/50 hover:text-blue-700 transition-all text-left group">
                      <span className="text-xl filter group-hover:scale-110 transition-transform">{f.emoji}</span>
                      <span className="text-sm font-medium">{f.label}</span>
                      <Plus className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-12 bg-white">
              <div className="max-w-2xl mx-auto space-y-8">
                {/* Mobile Field Type Selector */}
                <div className="lg:hidden bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400">Add New Field</label>
                  <Select onValueChange={(val) => addField(val)}>
                    <SelectTrigger className="w-full h-12 bg-white border-slate-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Plus className="w-4 h-4 text-primary" />
                        <SelectValue placeholder="Select a field type to add" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {FIELD_TYPES.map((f) => (
                        <SelectItem key={f.type} value={f.type}>
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{f.emoji}</span>
                            <span className="font-medium">{f.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-white p-6 md:p-8 rounded-2xl border border-border shadow-sm space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 text-left">Form Name</label>
                      <input type="text" placeholder="Enter form name" className="w-full px-4 py-2.5 bg-input-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" value={formName} onChange={(e) => setFormName(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 text-left">Form Description</label>
                      <textarea placeholder="Enter form description" rows={2} className="w-full px-4 py-2.5 bg-input-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none" value={formDescription} onChange={(e) => setFormDescription(e.target.value)}></textarea>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 md:p-8 rounded-2xl border border-border shadow-sm space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-border mb-4">
                    <BookOpen className="w-5 h-5 text-primary" />
                    <h3 className="font-bold text-gray-900">Request Procedures</h3>
                  </div>
                  <div className="space-y-4">
                    <p className="text-xs text-muted-foreground font-medium tracking-tight leading-relaxed">
                      Select procedures that will help agents resolve requests submitted via this form. These procedures will be prominently displayed to the assigned agent.
                    </p>
                    <div className="grid gap-2">
                      <MultiSelect
                        options={combinedProcedures
                          .map(proc => ({
                            label: proc.procedureName,
                            value: String(proc.id),
                          }))}
                        selected={linkedProcedureIds}
                        onChange={(values) => setLinkedProcedureIds(values)}
                        placeholder="Select linked procedures..."
                        emptyMessage="No procedures available or you don't have permission to select them."
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {fields.length > 0 ? fields.map((field, index) => (
                    <div 
                      key={field.id} 
                      onClick={() => setSelectedFieldId(field.id)} 
                      className={cn("content-field-card group relative bg-white p-6 rounded-2xl border transition-all cursor-pointer text-left", selectedFieldId === field.id ? "border-primary ring-1 ring-primary shadow-md" : "border-border hover:border-primary/50 shadow-sm")}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1 pt-2">
                          {index > 0 && <button onClick={(e) => { e.stopPropagation(); moveField(index, 'up'); }} className="p-1 hover:bg-accent rounded transition-colors" title="Move up"><ArrowUp className="w-4 h-4 text-muted-foreground" /></button>}
                          {index < fields.length - 1 && <button onClick={(e) => { e.stopPropagation(); moveField(index, 'down'); }} className="p-1 hover:bg-accent rounded transition-colors" title="Move down"><ArrowDown className="w-4 h-4 text-muted-foreground" /></button>}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2"><span className="text-xs px-2 py-1 bg-muted rounded text-muted-foreground">{FIELD_TYPES.find(f => f.type === field.type)?.label}</span></div>
                          {field.type === 'heading' ? (
                            <div className={cn("pb-1 mb-2", field.showDivider !== false ? "border-b border-gray-100" : "")}>
                              <div className="text-lg font-bold text-gray-900">{field.label || "New Heading"}</div>
                              {field.description && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{field.description}</div>}
                            </div>
                          ) : field.type === 'static_text' ? (
                            <div className={cn(
                              "rounded-xl transition-all",
                              field.showContainer ? cn(
                                "p-4 border",
                                field.containerColor === 'blue' ? "bg-blue-50/50 border-blue-100/50 text-blue-900" :
                                field.containerColor === 'green' ? "bg-green-50/50 border-green-100/50 text-green-900" :
                                field.containerColor === 'amber' ? "bg-amber-50/50 border-amber-100/50 text-amber-900" :
                                field.containerColor === 'red' ? "bg-red-50/50 border-red-100/50 text-red-900" :
                                "bg-gray-50/50 border-gray-100/50 text-gray-900"
                              ) : "p-0"
                            )}>
                              {field.label && (
                                <div className={cn(
                                  "font-bold text-sm mb-1",
                                  field.showContainer ? "" : "text-gray-900"
                                )}>
                                  {field.label}
                                </div>
                              )}
                              <div className={cn(
                                "text-xs whitespace-pre-wrap leading-relaxed",
                                field.showContainer ? "opacity-90" : "text-gray-600"
                              )}>
                                {field.description}
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="font-medium mb-1">{field.label || "New field"} {field.required && <span className="text-red-500">*</span>}</div>
                              {field.description && <div className="text-xs text-muted-foreground mb-2 whitespace-pre-wrap">{field.description}</div>}
                              <div className="text-sm text-gray-400 italic flex items-center gap-2">{field.type === 'dropdown' && <ChevronDown className="w-4 h-4 text-gray-400" />}{field.placeholder || (field.type === 'dropdown' ? "Select an option" : "")}</div>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setSelectedFieldId(field.id);
                              setIsPropertiesModalOpen(true);
                            }} 
                            className="lg:hidden p-2 hover:bg-accent rounded-lg transition-colors text-muted-foreground hover:text-primary"
                          >
                            <SquarePen className="w-4 h-4" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); removeField(field.id); }} className="p-2 hover:bg-accent rounded-lg transition-colors"><Trash2 className="w-4 h-4 text-muted-foreground" /></button>
                        </div>
                      </div>
                    </div>
                  )) : <div className="text-center py-12 border-2 border-dashed border-border rounded-lg"><p className="text-muted-foreground text-sm font-medium">Add fields from the top selector to build your form</p></div>}
                </div>
              </div>
            </div>
            {/* Desktop Right Sidebar - Hidden on mobile */}
            <div className="hidden lg:block w-80 border-l border-border bg-white overflow-y-auto shrink-0 text-left">
              {selectedField ? (
                <div className="p-6 space-y-6">
                  <div className="flex items-center gap-2 mb-2"><Settings2 className="w-4 h-4 text-primary" /><h3 className="font-bold text-gray-900">Field Properties</h3></div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        {selectedField.type === 'heading' ? 'Heading Text' : selectedField.type === 'static_text' ? 'Title (Optional)' : 'Field Label'}
                      </label>
                      <input type="text" className="w-full px-3 py-2 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" value={selectedField.label} onChange={(e) => updateField(selectedField.id, { label: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        {selectedField.type === 'static_text' ? 'Static Text Content' : 'Field Description'}
                      </label>
                      <textarea className="w-full px-3 py-2 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none" rows={selectedField.type === 'static_text' ? 6 : 3} placeholder={selectedField.type === 'static_text' ? "Enter the text to display on the form" : "Enter description"} value={selectedField.description} onChange={(e) => updateField(selectedField.id, { description: e.target.value })} />
                    </div>
                    {selectedField.type === 'heading' && (
                      <div className="flex items-center justify-between p-3 bg-input-background rounded-xl border border-border">
                        <span className="text-sm font-medium">Show Divider</span>
                        <button onClick={() => updateField(selectedField.id, { showDivider: !selectedField.showDivider })} className={cn("w-10 h-5 rounded-full transition-colors relative", selectedField.showDivider ? "bg-primary" : "bg-gray-300")}>
                          <div className={cn("absolute top-1 w-3 h-3 bg-white rounded-full transition-transform", selectedField.showDivider ? "left-6" : "left-1")}></div>
                        </button>
                      </div>
                    )}
                    {!['heading', 'static_text'].includes(selectedField.type) && (
                      <>
                        <div><label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Placeholder</label><input type="text" className="w-full px-3 py-2 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" value={selectedField.placeholder} onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })} /></div>
                        
                        {selectedField.type === 'idarah_data' && (
                          <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Selection Mode</label>
                              <Select 
                                value={selectedField.selectionMode} 
                                onValueChange={(val: 'single' | 'multi') => updateField(selectedField.id, { selectionMode: val })}
                              >
                                <SelectTrigger className="w-full h-9 bg-input-background border-border rounded-lg text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="single">Single Select</SelectItem>
                                  <SelectItem value="multi">Multi Select</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Idarah Data Source</label>
                              <Select 
                                value={selectedField.dataSource} 
                                onValueChange={(val) => updateField(selectedField.id, { dataSource: val })}
                              >
                                <SelectTrigger className="w-full h-9 bg-input-background border-border rounded-lg text-sm text-left">
                                  <SelectValue placeholder="Select data source" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="businessSystems">Business Systems</SelectItem>
                                  <SelectItem value="assets">Assets</SelectItem>
                                  <SelectItem value="inventoryItems">Inventory Items</SelectItem>
                                  <SelectItem value="qualifications">Qualifications</SelectItem>
                                  <SelectItem value="trainingPlans">Training Plans</SelectItem>
                                  <SelectItem value="jobRoles">Job Roles</SelectItem>
                                  <SelectItem value="procedures">Procedures</SelectItem>
                                  <SelectItem value="projects">Projects</SelectItem>
                                  <SelectItem value="branches">Branches</SelectItem>
                                  <SelectItem value="customers">Customers</SelectItem>
                                  <SelectItem value="suppliers">Suppliers</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-3 bg-input-background rounded-xl border border-border"><span className="text-sm font-medium">Required Field</span><button onClick={() => updateField(selectedField.id, { required: !selectedField.required })} className={cn("w-10 h-5 rounded-full transition-colors relative", selectedField.required ? "bg-primary" : "bg-gray-300")}><div className={cn("absolute top-1 w-3 h-3 bg-white rounded-full transition-transform", selectedField.required ? "left-6" : "left-1")}></div></button></div>
                      </>
                    )}
                    {selectedField.type === 'static_text' && (
                      <div className="space-y-4 pt-2 border-t border-border">
                        <div className="flex items-center justify-between p-3 bg-input-background rounded-xl border border-border">
                          <span className="text-sm font-medium">Show Container</span>
                          <button onClick={() => updateField(selectedField.id, { showContainer: !selectedField.showContainer })} className={cn("w-10 h-5 rounded-full transition-colors relative", selectedField.showContainer ? "bg-primary" : "bg-gray-300")}>
                            <div className={cn("absolute top-1 w-3 h-3 bg-white rounded-full transition-transform", selectedField.showContainer ? "left-6" : "left-1")}></div>
                          </button>
                        </div>
                        
                        {selectedField.showContainer && (
                          <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Container Color</label>
                            <div className="flex items-center gap-2">
                              {(['gray', 'blue', 'green', 'amber', 'red'] as const).map((color) => (
                                <button
                                  key={color}
                                  onClick={() => updateField(selectedField.id, { containerColor: color })}
                                  className={cn(
                                    "w-8 h-8 rounded-full border-2 transition-all",
                                    color === 'gray' ? "bg-gray-100 border-gray-200" : 
                                    color === 'blue' ? "bg-blue-100 border-blue-200" :
                                    color === 'green' ? "bg-green-100 border-green-200" :
                                    color === 'amber' ? "bg-amber-100 border-amber-200" :
                                    "bg-red-100 border-red-200",
                                    selectedField.containerColor === color ? "border-primary scale-110 shadow-sm" : "border-transparent hover:scale-105"
                                  )}
                                  title={color.charAt(0).toUpperCase() + color.slice(1)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedField.options && (
                      <div className="space-y-3">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Options</label>
                        {selectedField.options.map((opt, idx) => (
                          <div key={idx} className="flex gap-2"><input type="text" className="flex-1 px-3 py-1.5 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" value={opt} onChange={(e) => { const newOptions = [...selectedField.options!]; newOptions[idx] = e.target.value; updateField(selectedField.id, { options: newOptions }); }} /><button onClick={() => { const newOptions = selectedField.options!.filter((_, i) => i !== idx); updateField(selectedField.id, { options: newOptions }); }} className="p-1.5 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button></div>
                        ))}
                        <Button variant="outline" size="sm" className="w-full text-xs h-8 border-dashed" onClick={() => updateField(selectedField.id, { options: [...selectedField.options!, `Option ${selectedField.options!.length + 1}`] })}><Plus className="w-3 h-3 mr-1" /> Add Option</Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center text-muted-foreground"><div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4"><Settings2 className="w-6 h-6 text-gray-300" /></div><p className="text-sm">Select a field to edit its properties</p></div>
              )}
            </div>

            {/* Mobile Properties Modal */}
            <Dialog open={isPropertiesModalOpen} onOpenChange={setIsPropertiesModalOpen}>
              <DialogContent className="sm:max-w-md bg-white p-0 overflow-hidden border-none shadow-2xl">
                <DialogHeader className="p-6 border-b border-border flex-row items-center gap-3 space-y-0 text-left">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Settings2 className="w-5 h-5 text-primary" />
                  </div>
                  <DialogTitle className="text-xl font-bold">Field Properties</DialogTitle>
                </DialogHeader>

                <div className="p-6 overflow-y-auto max-h-[70vh]">
                  {selectedField ? (
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                            {selectedField.type === 'heading' ? 'Heading Text' : selectedField.type === 'static_text' ? 'Title (Optional)' : 'Field Label'}
                          </label>
                          <input type="text" className="w-full px-3 py-2 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" value={selectedField.label} onChange={(e) => updateField(selectedField.id, { label: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                            {selectedField.type === 'static_text' ? 'Static Text Content' : 'Field Description'}
                          </label>
                          <textarea className="w-full px-3 py-2 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none" rows={selectedField.type === 'static_text' ? 6 : 3} placeholder={selectedField.type === 'static_text' ? "Enter the text to display on the form" : "Enter description"} value={selectedField.description} onChange={(e) => updateField(selectedField.id, { description: e.target.value })} />
                        </div>
                        {selectedField.type === 'heading' && (
                          <div className="flex items-center justify-between p-3 bg-input-background rounded-xl border border-border">
                            <span className="text-sm font-medium">Show Divider</span>
                            <button onClick={() => updateField(selectedField.id, { showDivider: !selectedField.showDivider })} className={cn("w-10 h-5 rounded-full transition-colors relative", selectedField.showDivider ? "bg-primary" : "bg-gray-300")}>
                              <div className={cn("absolute top-1 w-3 h-3 bg-white rounded-full transition-transform", selectedField.showDivider ? "left-6" : "left-1")}></div>
                            </button>
                          </div>
                        )}
                        {!['heading', 'static_text'].includes(selectedField.type) && (
                          <>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Placeholder</label><input type="text" className="w-full px-3 py-2 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" value={selectedField.placeholder} onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })} /></div>
                            
                            {selectedField.type === 'idarah_data' && (
                              <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                                <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Selection Mode</label>
                                  <Select 
                                    value={selectedField.selectionMode} 
                                    onValueChange={(val: 'single' | 'multi') => updateField(selectedField.id, { selectionMode: val })}
                                  >
                                    <SelectTrigger className="w-full h-11 bg-input-background border-border rounded-lg text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="single">Single Select</SelectItem>
                                      <SelectItem value="multi">Multi Select</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Idarah Data Source</label>
                                  <Select 
                                    value={selectedField.dataSource} 
                                    onValueChange={(val) => updateField(selectedField.id, { dataSource: val })}
                                  >
                                    <SelectTrigger className="w-full h-11 bg-input-background border-border rounded-lg text-sm text-left">
                                      <SelectValue placeholder="Select data source" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="businessSystems">Business Systems</SelectItem>
                                      <SelectItem value="assets">Assets</SelectItem>
                                      <SelectItem value="inventoryItems">Inventory Items</SelectItem>
                                      <SelectItem value="qualifications">Qualifications</SelectItem>
                                      <SelectItem value="trainingPlans">Training Plans</SelectItem>
                                      <SelectItem value="jobRoles">Job Roles</SelectItem>
                                      <SelectItem value="procedures">Procedures</SelectItem>
                                      <SelectItem value="projects">Projects</SelectItem>
                                      <SelectItem value="branches">Branches</SelectItem>
                                      <SelectItem value="customers">Customers</SelectItem>
                                      <SelectItem value="suppliers">Suppliers</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}

                            <div className="flex items-center justify-between p-3 bg-input-background rounded-xl border border-border"><span className="text-sm font-medium">Required Field</span><button onClick={() => updateField(selectedField.id, { required: !selectedField.required })} className={cn("w-10 h-5 rounded-full transition-colors relative", selectedField.required ? "bg-primary" : "bg-gray-300")}><div className={cn("absolute top-1 w-3 h-3 bg-white rounded-full transition-transform", selectedField.required ? "left-6" : "left-1")}></div></button></div>
                          </>
                        )}
                        {selectedField.type === 'static_text' && (
                          <div className="space-y-4 pt-2 border-t border-border">
                            <div className="flex items-center justify-between p-3 bg-input-background rounded-xl border border-border">
                              <span className="text-sm font-medium">Show Container</span>
                              <button onClick={() => updateField(selectedField.id, { showContainer: !selectedField.showContainer })} className={cn("w-10 h-5 rounded-full transition-colors relative", selectedField.showContainer ? "bg-primary" : "bg-gray-300")}>
                                <div className={cn("absolute top-1 w-3 h-3 bg-white rounded-full transition-transform", selectedField.showContainer ? "left-6" : "left-1")}></div>
                              </button>
                            </div>
                            
                            {selectedField.showContainer && (
                              <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Container Color</label>
                                <div className="flex items-center gap-2">
                                  {(['gray', 'blue', 'green', 'amber', 'red'] as const).map((color) => (
                                    <button
                                      key={color}
                                      onClick={() => updateField(selectedField.id, { containerColor: color })}
                                      className={cn(
                                        "w-8 h-8 rounded-full border-2 transition-all",
                                        color === 'gray' ? "bg-gray-100 border-gray-200" : 
                                        color === 'blue' ? "bg-blue-100 border-blue-200" :
                                        color === 'green' ? "bg-green-100 border-green-200" :
                                        color === 'amber' ? "bg-amber-100 border-amber-200" :
                                        "bg-red-100 border-red-200",
                                        selectedField.containerColor === color ? "border-primary scale-110 shadow-sm" : "border-transparent hover:scale-105"
                                      )}
                                      title={color.charAt(0).toUpperCase() + color.slice(1)}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {selectedField.options && (
                          <div className="space-y-3">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Options</label>
                            {selectedField.options.map((opt, idx) => (
                              <div key={idx} className="flex gap-2"><input type="text" className="flex-1 px-3 py-1.5 text-sm bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" value={opt} onChange={(e) => { const newOptions = [...selectedField.options!]; newOptions[idx] = e.target.value; updateField(selectedField.id, { options: newOptions }); }} /><button onClick={() => { const newOptions = selectedField.options!.filter((_, i) => i !== idx); updateField(selectedField.id, { options: newOptions }); }} className="p-1.5 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button></div>
                            ))}
                            <Button variant="outline" size="sm" className="w-full text-xs h-8 border-dashed" onClick={() => updateField(selectedField.id, { options: [...selectedField.options!, `Option ${selectedField.options!.length + 1}`] })}><Plus className="w-3 h-3 mr-1" /> Add Option</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground"><p>No field selected</p></div>
                  )}
                </div>

                <DialogFooter className="p-6 border-t border-border bg-muted/30">
                  <Button onClick={() => setIsPropertiesModalOpen(false)} className="w-full sm:w-auto px-8 font-bold">
                    Done
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
